import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ContextMenuComplete, OutlinedIcons } from '@marageti/z4-lib';
import { SignificantDate } from '@marageti/z4-sdk/lib/people';
import formatSignificantDate from './utils';

const { OutlinedStarIcon } = OutlinedIcons;

type SignificantDatesViewProps = {
  dates: SignificantDate[];
  onAdd?: () => void;
  onEdit?: (entry: SignificantDate) => void;
  onDelete?: (id: string) => void;
};

const SignificantDatesView = ({
  dates,
  onAdd,
  onEdit,
  onDelete,
}: SignificantDatesViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <OutlinedStarIcon fontSize="small" />
        <Typography variant="body">Significant Dates</Typography>
      </Stack>

      {dates.length === 0 && (
        <Typography variant="body" color="common.black50">Not recorded</Typography>
      )}

      {dates.length > 0 && (
      <Stack spacing={0}>
        {dates.map((entry) => (
          <Box
            key={entry.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.25,
              '& .sig-date-menu': { opacity: isMobile ? 1 : 0, transition: 'opacity 0.15s' },
              '&:hover .sig-date-menu': { opacity: 1 },
            }}
          >
            <Typography variant="body" sx={{ flex: 1 }}>
              {entry.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
              {formatSignificantDate(entry.date)}
            </Typography>
            {(onEdit || onDelete) && (
              <Box className="sig-date-menu">
                <ContextMenuComplete
                  id={`sig-date-menu-${entry.id}`}
                  items={[
                    ...(onEdit ? [{ label: 'Edit', action: () => onEdit(entry) }] : []),
                    ...(onDelete ? [{ label: 'Delete', action: () => onDelete(entry.id) }] : []),
                  ]}
                />
              </Box>
            )}
          </Box>
        ))}
      </Stack>
      )}

      {onAdd && (
        <Button
          variant="text"
          size="small"
          onClick={onAdd}
          sx={{ p: 0, minWidth: 0, alignSelf: 'flex-start' }}
        >
          <Typography variant="captionBold" color="primary.main">+Add Date</Typography>
        </Button>
      )}
    </Stack>
  );
};

export default SignificantDatesView;
